/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { Theme } from '@coursera/cds-core';
import { Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import EnrollmentChoiceModalOptionListContainer from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalOptionListContainer';
import type { EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery_EnterpriseProgramSessionAssociationsV1Resource_byProgramsAndCourse_elements as EnterpriseProgramSessionAssociation } from 'bundles/enterprise-legacy-learner-home/constants/__generated__/EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery';
import { CreditBadge } from 'bundles/enterprise-ui/components/Badges';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type PropsFromCaller = {
  productType: string;
  programJoinTrackingVerbiage: boolean;
  thirdPartyOrgName: string;
  programOption: ProgramOption;
  programsWithCredits?: Array<string | undefined>;
  enterpriseProgramAssociations?: { [key: string]: EnterpriseProgramSessionAssociation };
};

type ProgramOption = {
  programId: string;
  programName: string;
  date?: Date;
  isInvitation: boolean;
};

type Props = PropsFromCaller;

const styles = {
  programListItem: (theme: Theme) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
    margin: 'var(--cds-spacing-200) 0',
    [breakpoints.down('xs')]: {
      margin: 'var(--cds-spacing-200) 0',
    },
  }),
  programListItemLabel: {
    flexGrow: 1,
  },
};

const EnrollmentChoiceModalSingleProgram = ({
  productType,
  programJoinTrackingVerbiage,
  programOption,
  thirdPartyOrgName,
  programsWithCredits,
  enterpriseProgramAssociations,
}: Props) => {
  const theme = useTheme();

  const description = (
    <>
      <FormattedMessage
        message={_t(
          'This {productType, select, course {course} s12n {specialization}} is available in the learning program created by your organization, {thirdPartyOrgName}.'
        )}
        productType={productType}
        thirdPartyOrgName={thirdPartyOrgName}
      />{' '}
      {programJoinTrackingVerbiage &&
        _t('Your course grades will be recorded and progress will be tracked in the following program:')}
    </>
  );

  // ---
  if (!programOption) {
    return (
      <EnrollmentChoiceModalOptionListContainer.Placeholder
        title={null}
        description={description}
        isMultiChoice={false}
      />
    );
  }

  const session = enterpriseProgramAssociations?.[programOption.programId]?.session;
  return (
    <EnrollmentChoiceModalOptionListContainer description={description} isMultiChoice={false}>
      <div css={styles.programListItem(theme)}>
        <div css={styles.programListItemLabel}>
          <Typography2 component="span">{programOption.programName}</Typography2>
          {(programOption.date || session) && (
            <>
              <br />
              <Typography2 variant="bodySecondary" color="supportText" component="span">
                {session ? (
                  <FormattedMessage
                    message={_t('{startDate, date, long} - {endDate, date, long}')}
                    startDate={session.startsAt}
                    endDate={session.endsAt}
                  />
                ) : (
                  <FormattedMessage
                    message={_t('Program {isInvitation, select, true {invite} false {join}} date: {date, date, long}')}
                    date={programOption.date}
                    isInvitation={programOption.isInvitation}
                  />
                )}
              </Typography2>
            </>
          )}
          <br />
          {programsWithCredits?.includes(programOption.programId) && <CreditBadge />}
        </div>
      </div>
    </EnrollmentChoiceModalOptionListContainer>
  );
};

export default EnrollmentChoiceModalSingleProgram;
